import React, {useEffect, useState, useRef} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {Route, Switch, useRouteMatch, Redirect, useLocation, useHistory} from 'react-router-dom'

import {
    Box,
    Button,
    Grid,
    Heading,
    Stack,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import {ChevronRightIcon} from '@salesforce/retail-react-app/app/components/icons'
import AccountDetail from '../account/profile'
import AddressBook from './addressBook'
import AccountOrders from './orders'
import ChangePassword from './changePassword'
import AccountWishlist from './wishlist/index'
import {messages, getNavLinks} from './constant'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import useEinstein from '@salesforce/retail-react-app/app/hooks/use-einstein'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'


import {useAuthHelper, AuthHelpers, useCustomer} from '@salesforce/commerce-sdk-react'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import {useAccountEdit} from '../../contexts/account-edit-context'
import {useScript, setInitialScriptState} from '../../contexts/script-context'

import LoyaltyPoints from '../../components/loyalty-points'
import BarcodeComponent from '../../components/barcode-component'
import LeaveModal from '../../components/leave-modal'
import MaintenancePage from '../../components/maintenance-page'
import PaymentDetails from './payment-details'
import CommunicationPreferences from './communication-preferences'
import Seo from '../../components/seo'
import RouteGuard from '../../components/route-guard'

const onClient = typeof window !== 'undefined'

const {app: globalConfig} = getConfig()

if (globalConfig.IS_NOIBU_DISABLED_ACCOUNT === 'true') {
    setInitialScriptState('noibu', false)
}

const AccountContent = () => {
    const {enableScript, disableScript} = useScript()
    const config = getConfig()
    const {path} = useRouteMatch()
    const {formatMessage} = useIntl()
    const {data: customer} = useCurrentCustomer()
    const {isRegistered, customerType, customerId} = customer
    const customerObj = useCustomer(
        {parameters: {customerId}},
        {enabled: isRegistered && !!customerId}
    )

    // Prevent hydration mismatch - customer data is only available on client after API call
    const [hasMounted, setHasMounted] = useState(false)
    useEffect(() => {
        setHasMounted(true)
    }, [])

    const logout = useAuthHelper(AuthHelpers.Logout)
    const location = useLocation()
    const history = useHistory()
    const navigate = useNavigation()
    const pathname = location.pathname

    const einstein = useEinstein()
    const {getTokenWhenReady} = useAccessToken()
    const appOrigin = useAppOrigin()
    const {buildUrl} = useMultiSite()
    const loginPath = buildUrl('/login')

    const seoByRoute = {
        personal: {
            title: formatMessage({
                id: 'personal_info.page.title',
                defaultMessage: 'My Account Details - Garage Clothing UK'
            }),
            description: formatMessage({
                id: 'personal_info.page.description',
                defaultMessage: 'My Account Details - Garage Clothing UK'
            }),
            noIndex: true,
            noFollow: true
        },
        addresses: {
            title: formatMessage({
                id: 'address_book.page.title',
                defaultMessage: 'My Account Details - Garage Clothing UK'
            }),
            description: formatMessage({
                id: 'address_book.page.description',
                defaultMessage: 'My Account Details - Garage Clothing UK'
            }),
            noIndex: true,
            noFollow: true
        },
        payment: {
            title: formatMessage({
                id: 'payment_details.page.title',
                defaultMessage: 'My Account Details - Garage Clothing UK'
            }),
            description: formatMessage({
                id: 'payment_details.page.description',
                defaultMessage: 'My Account Details - Garage Clothing UK'
            }),
            noIndex: true,
            noFollow: true
        },
        wishlist: {
            title: formatMessage({
                id: 'my.wishlist.page.title',
                defaultMessage: 'My Wishlist | Garage Clothing UK'
            }),
            description: formatMessage({
                id: 'my.wishlist.page.description',
                defaultMessage: 'Use the Garage Clothing wishlist to save your favourite styles.'
            }),
            noIndex: true,
            noFollow: true
        },
        orders: {
            title: formatMessage({
                id: 'account.order.history.page.title',
                defaultMessage: 'My Order History | Garage Clothing UK'
            }),
            description: formatMessage({
                id: 'order_history.page.description',
                defaultMessage: 'Track your Order at Garage Clothing UK.'
            }),
            openGraphTags: [
                {
                    property: 'og:title',
                    content: formatMessage({
                        id: 'account.order.history.page.title',
                        defaultMessage: 'My Order History | Garage Clothing UK'
                    })
                },
                {
                    property: 'og:description',
                    content: formatMessage({
                        id: 'order_history.page.description',
                        defaultMessage: 'Track your Order at Garage Clothing UK.'
                    })
                }
            ],
            noIndex: true,
            noFollow: true
        },
        password: {
            title: formatMessage({
                id: 'change_password.page.title',
                defaultMessage: 'My Account Details - Garage Clothing UK'
            }),
            description: formatMessage({
                id: 'change_password.page.description',
                defaultMessage: 'My Account Details - Garage Clothing UK'
            }),
            noIndex: true,
            noFollow: true
        },
        email: {
            title: formatMessage({
                id: 'communication_preferences.page.title',
                defaultMessage: 'My Account Details - Garage Clothing UK'
            }),
            description: formatMessage({
                id: 'communication_preferences.page.description',
                defaultMessage: 'My Account Details - Garage Clothing UK'
            }),
            noIndex: true,
            noFollow: true
        }
    }

    const renderGuarded = (seoProps, content) => (
        <>
            <Seo {...seoProps} />
            <RouteGuard redirectTo={loginPath}>{content}</RouteGuard>
        </>
    )

    const maintenancePageIds =
        (config.app.MAINTENANCE_ACCOUNT_WEB_ENABLED === true ||
            config.app.MAINTENANCE_ACCOUNT_WEB_ENABLED === 'true') &&
        config.app.MAINTENANCE_PAGE_IDS
            ? config.app.MAINTENANCE_PAGE_IDS
            : ''
    const allKeys = ['personal', 'addresses', 'payment', 'wishlist', 'orders', 'password', 'email']
    const enabledKeys = maintenancePageIds.split(',').map((key) => key.trim())

    const maintenanceConfig = Object.fromEntries(
        allKeys.map((key) => [key, enabledKeys.includes(key)])
    )

    const maintenanceTexts = {
        personal: {
            title: formatMessage({
                id: 'maintenance_page.personal.title',
                defaultMessage: 'Personal Information Temporarily Unavailable'
            }),
            message: formatMessage({
                id: 'maintenance_page.general.message',
                defaultMessage: `We're sorry, but a service we rely on to perform this action is currently down. Our team has been notified and is working to resolve the issue. Please try again in a little while. In the meantime, feel free to browse our site and shop our new arrivals.`
            })
        },
        addresses: {
            title: formatMessage({
                id: 'maintenance_page.addresses.title',
                defaultMessage: 'Address Book Temporarily Unavailable'
            }),
            message: formatMessage({
                id: 'maintenance_page.general.message',
                defaultMessage: `We're sorry, but a service we rely on to perform this action is currently down. Our team has been notified and is working to resolve the issue. Please try again in a little while. In the meantime, feel free to browse our site and shop our new arrivals.`
            })
        },
        payment: {
            title: formatMessage({
                id: 'maintenance_page.payment.title',
                defaultMessage: 'Saved Payment Methods Temporarily Unavailable'
            }),
            message: formatMessage({
                id: 'maintenance_page.general.message',
                defaultMessage: `We're sorry, but a service we rely on to perform this action is currently down. Our team has been notified and is working to resolve the issue. Please try again in a little while. In the meantime, feel free to browse our site and shop our new arrivals.`
            })
        },
        wishlist: {
            title: formatMessage({
                id: 'maintenance_page.wishlist.title',
                defaultMessage: 'Wishlist Temporarily Unavailable'
            }),
            message: formatMessage({
                id: 'maintenance_page.general.message',
                defaultMessage: `We're sorry, but a service we rely on to perform this action is currently down. Our team has been notified and is working to resolve the issue. Please try again in a little while. In the meantime, feel free to browse our site and shop our new arrivals.`
            })
        },
        orders: {
            title: formatMessage({
                id: 'maintenance_page.orders.title',
                defaultMessage: 'Order History Temporarily Unavailable'
            }),
            message: formatMessage({
                id: 'maintenance_page.general.message',
                defaultMessage: `We're sorry, but a service we rely on to perform this action is currently down. Our team has been notified and is working to resolve the issue. Please try again in a little while. In the meantime, feel free to browse our site and shop our new arrivals.`
            })
        },
        password: {
            title: formatMessage({
                id: 'maintenance_page.password.title',
                defaultMessage: 'Change Password Temporarily Unavailable'
            }),
            message: formatMessage({
                id: 'maintenance_page.general.message',
                defaultMessage: `We're sorry, but a service we rely on to perform this action is currently down. Our team has been notified and is working to resolve the issue. Please try again in a little while. In the meantime, feel free to browse our site and shop our new arrivals.`
            })
        },
        email: {
            title: formatMessage({
                id: 'maintenance_page.email.title',
                defaultMessage: 'Communication Preferences Temporarily Unavailable'
            }),
            message: formatMessage({
                id: 'maintenance_page.general.message',
                defaultMessage: `We're sorry, but a service we rely on to perform this action is currently down. Our team has been notified and is working to resolve the issue. Please try again in a little while. In the meantime, feel free to browse our site and shop our new arrivals.`
            })
        }
    }

    useEffect(() => {
        if (config.app.IS_NOIBU_DISABLED_ACCOUNT === 'true') {
            disableScript('noibu')
        } else {
            enableScript('noibu')
        }

        return () => {
            if (config.app.IS_NOIBU_DISABLED_ACCOUNT) {
                enableScript('noibu')
            }
        }
    }, [])

    useEffect(() => {
        einstein.sendViewPage(location.pathname)
    }, [location])

    const onSignoutClick = async () => {
        const token = await getTokenWhenReady()
        await fetch(`${appOrigin}/process-logout`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({token})
        })
        await logout.mutateAsync()
        navigate('/')
    }

    const {isEditing, isDirty, stopEditing} = useAccountEdit()
    const [showLeaveModal, setShowLeaveModal] = useState(false)
    const pendingLocation = useRef(null)

    useEffect(() => {
        const unblock = history.block((tx) => {
            if (isEditing && isDirty) {
                setShowLeaveModal(true)
                pendingLocation.current = tx
                return false
            }
            return true
        })

        return () => unblock()
    }, [isEditing, isDirty, history])

    const handleDiscardChanges = () => {
        setShowLeaveModal(false)
        if (pendingLocation.current) {
            // Stop editing before navigation to prevent the block from triggering again
            stopEditing()
            // Use setTimeout to ensure state updates are processed before navigation
            setTimeout(() => {
                history.push(pendingLocation.current.pathname)
                pendingLocation.current = null
            }, 0)
        } else {
            stopEditing()
        }
    }

    const handleCloseModal = () => setShowLeaveModal(false)

    if (customerType !== null && !isRegistered && onClient) {
        return <Redirect to={{pathname: loginPath, state: {directedFrom: '/account'}}} />
    }

    // Only access customer data after mount to prevent hydration mismatch
    const firstName = hasMounted ? customerObj.data?.firstName : undefined
    const loyaltyID = hasMounted
        ? customerObj.data?.c_b2cloyalty_loyaltyProgramMemberNumber
        : undefined

    const renderContent = () => (
        <Switch>
            <Route exact path={path}>
                {renderGuarded(
                    seoByRoute.personal,
                    maintenanceConfig.personal ? (
                        <MaintenancePage {...maintenanceTexts.personal} />
                    ) : (
                        <AccountDetail />
                    )
                )}
            </Route>
            <Route exact path={`${path}/personal`}>
                {renderGuarded(
                    seoByRoute.personal,
                    maintenanceConfig.personal ? (
                        <MaintenancePage {...maintenanceTexts.personal} />
                    ) : (
                        <AccountDetail />
                    )
                )}
            </Route>
            <Route exact path={`${path}/wishlist`}>
                {renderGuarded(
                    seoByRoute.wishlist,
                    maintenanceConfig.wishlist ? (
                        <MaintenancePage {...maintenanceTexts.wishlist} />
                    ) : (
                        <AccountWishlist />
                    )
                )}
            </Route>
            <Route exact path={`${path}/addresses`}>
                {renderGuarded(
                    seoByRoute.addresses,
                    maintenanceConfig.addresses ? (
                        <MaintenancePage {...maintenanceTexts.addresses} />
                    ) : (
                        <AddressBook />
                    )
                )}
            </Route>
            <Route exact path={`${path}/payment`}>
                {renderGuarded(
                    seoByRoute.payment,
                    maintenanceConfig.payment ? (
                        <MaintenancePage {...maintenanceTexts.payment} />
                    ) : (
                        <PaymentDetails />
                    )
                )}
            </Route>
            <Route path={`${path}/orders`}>
                {renderGuarded(
                    seoByRoute.orders,
                    maintenanceConfig.orders ? (
                        <MaintenancePage {...maintenanceTexts.orders} />
                    ) : (
                        <AccountOrders />
                    )
                )}
            </Route>
            <Route path={`${path}/password`}>
                {renderGuarded(
                    seoByRoute.password,
                    maintenanceConfig.password ? (
                        <MaintenancePage {...maintenanceTexts.password} />
                    ) : (
                        <ChangePassword customerEmail={customerObj.data?.email} />
                    )
                )}
            </Route>
            {(config.app.SMS_OPTIN_LOYALTY_ENABLED == 'true' ||
                config.app.SFSC_EMAIL_SUBSCRIPTION_ENABLED == 'true') && (
                <Route path={`${path}/email`}>
                    {renderGuarded(
                        seoByRoute.email,
                        maintenanceConfig.email ? (
                            <MaintenancePage {...maintenanceTexts.email} />
                        ) : (
                            <CommunicationPreferences />
                        )
                    )}
                </Route>
            )}
        </Switch>
    )

    const isAccountSubPage = pathname !== '/account' && pathname.startsWith('/account')
    const isMainAccountPage = pathname === '/account'

    // Sidebar component - rendered once, shown only on desktop
    const Sidebar = () => (
        <RouteGuard redirectTo={loginPath}>
            <Stack id="account-left-container" spacing={12} display={{base: 'none', lg: 'flex'}}>
                <Box w="100%" maxW="225px">
                    <Heading as="h2" fontSize="md" mb={2} textTransform="uppercase">
                        <FormattedMessage
                            defaultMessage="Welcome"
                            id="account_dashboard.welcome"
                        />
                        , {firstName}
                    </Heading>

                    <LoyaltyPoints isAccountDrawer={false} loyaltyId={loyaltyID} />

                    <Link to="/my-rewards" display={'inline-block'}>
                        <Text mt={4} variant="accountInfoSmall" textDecoration="underline">
                            <FormattedMessage
                                defaultMessage="See My Rewards"
                                id="account.rewards"
                            />
                        </Text>
                    </Link>
                </Box>
                <Stack spacing={2} width="100%" maxW="225px">
                    {getNavLinks().map((link) => {
                        const fullPath = `/account${link.path}`
                        const isPersonal = link.path === '/personal'
                        const isActive =
                            (pathname === '/account' && isPersonal) || pathname === fullPath

                        return (
                            <Button
                                key={link.name}
                                as={Link}
                                to={fullPath}
                                useNavLink
                                variant="accountDashboardlink"
                                justifyContent="space-between"
                                borderRadius="none"
                                fontWeight={isActive ? 'medium' : 'normal'}
                                color={isActive ? 'black' : 'gray.700'}
                            >
                                {formatMessage(messages[link.name])}
                            </Button>
                        )
                    })}

                    <Button
                        onClick={onSignoutClick}
                        variant="unstyled"
                        cursor="pointer"
                        height="fit-content"
                        display="block"
                        textAlign="start"
                        color="gray.700"
                    >
                        <Text as="span" variant="accountLink">
                            {formatMessage({
                                defaultMessage: 'Log Out',
                                id: 'account.logout_button.button.log_out'
                            })}
                        </Text>
                    </Button>
                </Stack>
            </Stack>
        </RouteGuard>
    )

    // Mobile dashboard - only shown on main account page on mobile
    const MobileDashboard = () => (
        <Box display={{base: 'block', lg: 'none'}} padding="24px 16px 97px 16px">
            <Stack spacing={12} align="center" maxW="400px" mx="auto">
                <Box w="100%">
                    <Heading as="h2" fontSize="2xl" mb={2} textTransform="uppercase">
                        <FormattedMessage
                            defaultMessage="Welcome"
                            id="account_dashboard.welcome"
                        />
                        , {firstName}
                    </Heading>

                    {loyaltyID && <BarcodeComponent value={loyaltyID} />}
                    <LoyaltyPoints isAccountDrawer={false} />

                    <Link to="/my-rewards" display={'inline-block'}>
                        <Text mt={4} variant="accountInfoSmall" textDecoration="underline">
                            <FormattedMessage
                                defaultMessage="See My Rewards"
                                id="account.rewards"
                            />
                        </Text>
                    </Link>
                </Box>
                <Stack spacing={5} width="100%">
                    {getNavLinks().map((link) => (
                        <Button
                            key={link.name}
                            as={Link}
                            to={`/account${link.path}`}
                            useNavLink
                            variant="accountDashboardlink"
                            justifyContent="space-between"
                            rightIcon={<ChevronRightIcon />}
                            borderRadius="none"
                        >
                            {formatMessage(messages[link.name])}
                        </Button>
                    ))}
                </Stack>

                <Box width="100%">
                    <Button
                        onClick={onSignoutClick}
                        variant="unstyled"
                        cursor="pointer"
                        height="fit-content"
                        display="block"
                    >
                        <Text as="span" variant="accountLink">
                            {formatMessage({
                                defaultMessage: 'Log Out',
                                id: 'account.logout_button.button.log_out'
                            })}
                        </Text>
                    </Button>
                </Box>
            </Stack>
        </Box>
    )

    return (
        <Box data-testid={isRegistered ? 'account-page' : 'account-page-skeleton'}>

            {/* Mobile Dashboard - only on main /account page */}
            {isMainAccountPage && <MobileDashboard />}

            {/* Sub-pages: Single content render with responsive layout */}
            {isAccountSubPage && (
                <Grid
                    templateColumns={{base: '1fr', lg: '225px minmax(0, 1fr)'}}
                    gap={{base: 0, lg: 125}}
                    alignItems="flex-start"
                    maxW={{base: 'full', lg: 'container.xxxl'}}
                    mx="auto"
                    padding={{base: '24px 16px 97px 16px', lg: '48px 24px 97px 24px'}}
                >
                    <Sidebar />
                    <Box
                        flex={
                            pathname === '/account/payment' || pathname === '/account/addresses'
                                ? '1'
                                : ''
                        }
                    >
                        {renderContent()}
                    </Box>
                </Grid>
            )}

            {/* Main account page desktop layout - sidebar + profile content */}
            {isMainAccountPage && (
                <Grid
                    display={{base: 'none', lg: 'grid'}}
                    templateColumns="225px minmax(0, 1fr)"
                    gap={125}
                    alignItems="flex-start"
                    maxW="container.xxxl"
                    mx="auto"
                    padding="48px 24px 97px 24px"
                >
                    <Sidebar />
                    <Box>{renderContent()}</Box>
                </Grid>
            )}

            <LeaveModal
                isOpen={showLeaveModal}
                onClose={handleCloseModal}
                onConfirm={handleDiscardChanges}
            />
        </Box>
    )
}

const Account = () => {
    return <AccountContent />
}

Account.getTemplateName = () => 'account'
Account.propTypes = {match: PropTypes.object}

export default Account
